import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { keepPreviousData } from '@tanstack/react-query';
import {
  type ColumnFiltersState,
  type ColumnVisibilityState,
  createColumnHelper,
  flexRender,
  type SortingState,
  useTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useEdiCatalogItemList } from '@/api/hanly-v2/edi/edi';
import type {
  EdiCatalogItem,
  EdiCatalogItemListParams,
  ItemDisplayColumn,
} from '@/api/hanly-v2/hanlyAPIV2.schemas';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { ProductThumbnail } from '@/components/product-thumbnail';
import { FilterPanel, FilterToggleButton } from '@/components/ui/filter-panel';
import { Pagination } from '@/components/ui/pagination';
import { Sheet } from '@/components/ui/sheet';
import { type SortOption, SortSelect } from '@/components/ui/sort-select';
import { ColumnHeader } from '@/components/ui/table/column-header';
import { useShowPrices } from '@/contexts/auth';
import { ProductDetail } from '@/features/products/product-detail';
import { useIsDesktop } from '@/hooks/use-is-desktop';
import { ediTableFeatures } from '@/libs/table';

const columnHelper = createColumnHelper<typeof ediTableFeatures, EdiCatalogItem>();

// columns() で包むと、列ごとの TValue を保ったまま useTable に渡せる。
export const columns = columnHelper.columns([
  columnHelper.accessor('product_variation.code', {
    // ネストしたアクセサキーは列IDのドットが _ に置換されるため、APIのパラメータ名で固定する
    id: 'code',
    header: ({ column }) => <ColumnHeader column={column} title="商品コード" />,
    cell: (info) => info.getValue(),
    meta: { filterVariant: 'text' as const, label: '商品コード', placeholder: '例）A001' },
    size: 130,
  }),
  columnHelper.accessor('product_variation.name', {
    // ネストしたアクセサキーは列IDのドットが _ に置換されるため、APIのパラメータ名で固定する
    id: 'name',
    header: ({ column }) => <ColumnHeader column={column} title="商品名" />,
    cell: (info) => (
      <div className="flex min-w-0 items-center gap-2">
        <ProductThumbnail url={info.row.original.product_variation.thumbnail_url} size={40} />
        <span className="truncate">{info.getValue()}</span>
      </div>
    ),
    meta: { filterVariant: 'text' as const, label: '商品名', placeholder: '例）Tシャツ' },
    size: 400,
  }),
  columnHelper.accessor('product_variation.spec', {
    // ネストしたアクセサキーは列IDのドットが _ に置換されるため、APIのパラメータ名で固定する
    id: 'spec',
    header: ({ column }) => <ColumnHeader column={column} title="規格" />,
    cell: (info) => info.getValue() ?? '',
    meta: { filterVariant: 'text' as const, label: '規格', placeholder: '例）M' },
    size: 150,
  }),
  columnHelper.accessor('product_variation.model_number', {
    // ネストしたアクセサキーは列IDのドットが _ に置換されるため、APIのパラメータ名で固定する
    id: 'model_number',
    header: ({ column }) => <ColumnHeader column={column} title="品番" />,
    cell: (info) => info.getValue() ?? '',
    meta: { filterVariant: 'text' as const, label: '品番', placeholder: '例）MODEL-001' },
    size: 150,
  }),
  columnHelper.accessor('product_variation.unit_price', {
    // ネストしたアクセサキーは列IDのドットが _ に置換されるため、APIのパラメータ名で固定する
    id: 'unit_price',
    header: ({ column }) => <ColumnHeader column={column} title="単価" />,
    cell: (info) => {
      const value = info.getValue();
      return (
        <div className="text-right">
          <span className="text-sm">￥</span>
          {Number(value).toLocaleString()}
        </div>
      );
    },
    meta: { filterVariant: 'range' as const, label: '単価' },
    size: 130,
  }),
]);

const PAGE_SIZE = 50;

/** サーバサイドで並べ替え・絞り込みできる列ID(列定義で明示した id と一致させる) */
/**
 * 明細行の表示設定(item_display_columns)のキーと、この一覧の列IDの対応。
 * 表示設定で非表示にした項目はサーバが値を null にして返し、絞り込み・並べ替えも
 * 受け付けないため、列自体を出さない。
 */
const DISPLAY_KEY_TO_COLUMN_ID: Record<string, string> = {
  product_code: 'code',
  product_name: 'name',
  spec: 'spec',
  model_number: 'model_number',
  unit_price: 'unit_price',
};

const toColumnVisibility = (
  displayColumns: ItemDisplayColumn[] | undefined,
  showPrices: boolean,
): ColumnVisibilityState => {
  // 料金を表示しない発注先は単価列ごと出さない(サーバも単価を null で返し、絞り込み・並べ替えを受け付けない)
  const visibility: ColumnVisibilityState = showPrices ? {} : { unit_price: false };
  for (const column of displayColumns ?? []) {
    const columnId = DISPLAY_KEY_TO_COLUMN_ID[column.key];
    // required な項目は表示設定によらず常に表示する(サーバ側の get_hidden_fields と同じ判定)
    if (columnId && !column.visible && !column.required) {
      visibility[columnId] = false;
    }
  }
  return visibility;
};

const SERVER_SIDE_COLUMN_IDS = ['code', 'name', 'spec', 'model_number', 'unit_price'] as const;
/** 部分一致で絞り込む列 */
const TEXT_FILTER_COLUMNS = ['code', 'name', 'spec', 'model_number'] as const;

type ServerSideColumnId = (typeof SERVER_SIDE_COLUMN_IDS)[number];
type TextFilterColumn = (typeof TEXT_FILTER_COLUMNS)[number];

const isServerSideColumnId = (id: string): id is ServerSideColumnId =>
  (SERVER_SIDE_COLUMN_IDS as readonly string[]).includes(id);
const isTextFilterColumn = (id: string): id is TextFilterColumn =>
  (TEXT_FILTER_COLUMNS as readonly string[]).includes(id);

export const toOrdering = (sorting: SortingState): EdiCatalogItemListParams['ordering'] => {
  const [first] = sorting;
  if (!first || !isServerSideColumnId(first.id)) return undefined;
  return (first.desc ? `-${first.id}` : first.id) as EdiCatalogItemListParams['ordering'];
};

export const toFilterParams = (columnFilters: ColumnFiltersState): EdiCatalogItemListParams => {
  const params: EdiCatalogItemListParams = {};
  for (const filter of columnFilters) {
    if (isTextFilterColumn(filter.id)) {
      if (filter.value) params[filter.id] = String(filter.value);
      continue;
    }
    // 単価は [最小, 最大] を min/max のパラメータに割り当てる
    if (filter.id === 'unit_price' && Array.isArray(filter.value)) {
      const [min, max] = filter.value as (number | undefined)[];
      if (min !== undefined) params.unit_price_min = String(min);
      if (max !== undefined) params.unit_price_max = String(max);
    }
  }
  return params;
};

/** スマホの並べ替え。PC は表の見出しで並べ替える */
const sortOptions: (SortOption & { columnId: string | null })[] = [
  { value: '', label: '標準の並び順', columnId: null, sorting: [] },
  { value: 'name', label: '商品名順', columnId: 'name', sorting: [{ id: 'name', desc: false }] },
  { value: 'code', label: '商品コード順', columnId: 'code', sorting: [{ id: 'code', desc: false }] },
  {
    value: 'unit_price',
    label: '単価が安い順',
    columnId: 'unit_price',
    sorting: [{ id: 'unit_price', desc: false }],
  },
  {
    value: '-unit_price',
    label: '単価が高い順',
    columnId: 'unit_price',
    sorting: [{ id: 'unit_price', desc: true }],
  },
];

const ProductListRow = ({
  item,
  showPrices,
  onSelect,
}: {
  item: EdiCatalogItem;
  showPrices: boolean;
  onSelect: () => void;
}) => {
  const pv = item.product_variation;
  const meta = [pv.code, pv.spec, pv.model_number && `品番 ${pv.model_number}`].filter(Boolean).join(' ・ ');
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className="flex w-full cursor-pointer items-center gap-3 bg-white px-4 py-3 text-left"
      >
        <ProductThumbnail url={pv.thumbnail_url} size={64} />
        <span className="flex min-w-0 flex-1 flex-col leading-snug">
          <span className="font-bold">{pv.name}</span>
          {meta && <span className="text-sm text-neutral-600">{meta}</span>}
          {showPrices && (
            <span className="mt-0.5 font-bold">
              ¥{Number(pv.unit_price).toLocaleString()}
              {pv.unit_name && (
                <span className="text-sm font-normal text-neutral-600"> / {pv.unit_name}</span>
              )}
            </span>
          )}
        </span>
      </button>
    </li>
  );
};

export const ProductList = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [selectedProduct, setSelectedProduct] = useState<EdiCatalogItem | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const isDesktop = useIsDesktop();
  const showPrices = useShowPrices();

  const { data, isLoading, isError } = useEdiCatalogItemList(
    {
      page: pageIndex + 1,
      page_size: PAGE_SIZE,
      ordering: toOrdering(sorting),
      ...toFilterParams(columnFilters),
    },
    // 絞り込み・並べ替え・ページ送りのたびにクエリキーが変わる。前のデータを保持しないと
    // 再取得中に一覧(と検索欄)がアンマウントされ、1文字入力するたびにフォーカスが外れる。
    { query: { placeholderData: keepPreviousData } },
  );
  const items = data?.results ?? [];
  const itemDisplayColumns = data?.item_display_columns;
  const columnVisibility = useMemo(
    () => toColumnVisibility(itemDisplayColumns, showPrices),
    [itemDisplayColumns, showPrices],
  );
  const hasFilter = columnFilters.length > 0;
  const totalCount = data?.count ?? 0;

  // ソート・絞り込みを変えたら先頭ページに戻す
  const resetToFirstPage = () => setPageIndex(0);

  const table = useTable({
    features: ediTableFeatures,
    data: items,
    columns,
    state: { sorting, columnFilters, columnVisibility },
    onSortingChange: (updater) => {
      setSorting(updater);
      resetToFirstPage();
    },
    onColumnFiltersChange: (updater) => {
      setColumnFilters(updater);
      resetToFirstPage();
    },
    manualSorting: true,
    manualFiltering: true,
  });

  const nameColumn = table.getColumn('name');
  const visibleSortOptions = sortOptions.filter(
    (option) => option.columnId === null || columnVisibility[option.columnId] !== false,
  );
  const filterCount = columnFilters.filter((filter) => filter.id !== 'name').length;

  const statusMessage = isLoading
    ? { text: '読み込み中...', className: 'text-neutral-600' }
    : isError
      ? { text: 'データの取得に失敗しました', className: 'text-red-500' }
      : items.length === 0
        ? {
            text: hasFilter ? '該当する商品がありません' : '商品カタログに商品が登録されていません',
            className: 'text-neutral-600',
          }
        : null;

  return (
    <div className="flex-1">
      <Breadcrumbs items={[{ label: 'ホーム', to: '/' }, { label: '商品' }]} />

      <div className="flex flex-col gap-3 px-4 pt-4 pb-6 md:px-8">
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <label className="flex h-11 flex-1 items-center gap-2 border border-neutral-300 bg-white px-3 focus-within:border-sky-800 md:h-10 md:max-w-md">
            <MagnifyingGlassIcon className="size-5 shrink-0 text-neutral-400" />
            <input
              type="text"
              value={(nameColumn?.getFilterValue() as string) ?? ''}
              onChange={(e) => nameColumn?.setFilterValue(e.target.value)}
              placeholder="商品名で検索"
              aria-label="商品名で検索"
              className="border-0! px-0!"
            />
          </label>
          <div className="flex items-center gap-2">
            <FilterToggleButton
              open={isFilterOpen}
              onToggle={() => setIsFilterOpen((open) => !open)}
              activeCount={filterCount}
            />
            <SortSelect
              options={visibleSortOptions}
              sorting={sorting}
              onChange={table.setSorting}
              className="h-10 min-w-0 cursor-pointer border border-neutral-300 bg-white px-2 text-sm md:hidden"
            />
            {!isLoading && (
              <span className="ml-auto text-sm whitespace-nowrap text-neutral-600 md:ml-2">
                {totalCount.toLocaleString()}件
              </span>
            )}
          </div>
        </div>
        {isFilterOpen && <FilterPanel table={table} excludeColumnIds={['name']} />}

        <div className="flex items-start gap-5">
          <div className="min-w-0 flex-1">
            <ul className="-mx-4 divide-y divide-neutral-200 border-y border-neutral-200 md:hidden">
              {statusMessage ? (
                <li className={clsx('py-8 text-center', statusMessage.className)}>{statusMessage.text}</li>
              ) : (
                items.map((item) => (
                  <ProductListRow
                    key={item.id}
                    item={item}
                    showPrices={showPrices}
                    onSelect={() => setSelectedProduct(item)}
                  />
                ))
              )}
            </ul>

            <div className="hidden overflow-x-auto md:block">
              <table className="w-full table-fixed border border-neutral-200">
                <thead className="sticky top-0">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="whitespace-nowrap">
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="bg-neutral-100 px-3 py-2 text-left text-sm font-normal text-neutral-600"
                          style={{ width: header.getSize() }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {statusMessage ? (
                    <tr>
                      <td
                        colSpan={table.getVisibleLeafColumns().length}
                        className={clsx('py-8 text-center', statusMessage.className)}
                      >
                        {statusMessage.text}
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        className={clsx(
                          'group cursor-pointer',
                          selectedProduct?.id === row.original.id && 'bg-sky-50',
                        )}
                        onClick={() => setSelectedProduct(row.original)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className="truncate px-3 py-1.5 whitespace-nowrap text-neutral-900 group-hover:bg-neutral-100"
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              pageIndex={pageIndex}
              pageSize={PAGE_SIZE}
              totalCount={totalCount}
              onPageChange={setPageIndex}
            />
          </div>

          {isDesktop && selectedProduct && (
            <aside className="sticky top-4 flex w-80 shrink-0 flex-col border border-neutral-200 bg-white lg:w-96">
              <div className="flex items-start justify-between gap-2 border-b border-neutral-200 py-2 pr-1 pl-4">
                <h3 className="pt-1.5">{selectedProduct.product_variation.name}</h3>
                <button
                  type="button"
                  onClick={() => setSelectedProduct(null)}
                  aria-label="閉じる"
                  className="flex size-10 shrink-0 cursor-pointer items-center justify-center hover:text-neutral-600"
                >
                  <XMarkIcon className="size-5" />
                </button>
              </div>
              <div className="max-h-[calc(100vh-8rem)] overflow-y-auto p-4">
                <ProductDetail item={selectedProduct} />
              </div>
            </aside>
          )}
        </div>
      </div>

      {!isDesktop && (
        <Sheet
          open={selectedProduct !== null}
          onClose={() => setSelectedProduct(null)}
          title={selectedProduct?.product_variation.name ?? ''}
        >
          {selectedProduct && <ProductDetail item={selectedProduct} />}
        </Sheet>
      )}
    </div>
  );
};
